import { useEffect, useState } from 'react';
import { Box, Button, Paper, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { updateSingleConfigRequest } from '../../../redux/Configuration/configurationSlice';

const mapShowNames = (value) => {
  if (!Array.isArray(value) || !value.length) return [''];
  const names = value.map((item) =>
    String(typeof item === 'string' ? item : item?.name || ''),
  );
  return names.length ? names : [''];
};

const ShowNames = ({ canSave = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const configurationData = useSelector(
    (state) => state.appConfig.configurationData,
  );
  const [showNames, setShowNames] = useState(['']);

  useEffect(() => {
    if (!configurationData?.showNames) return;
    setShowNames(mapShowNames(configurationData.showNames));
  }, [configurationData]);

  const updateShowName = (index, value) => {
    setShowNames((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? value : item)),
    );
  };

  const handleAddShowName = () => {
    setShowNames((prev) => [...prev, '']);
  };

  const handleRemoveShowName = (index) => {
    setShowNames((prev) => {
      if (prev.length === 1) return [''];
      return prev.filter((_, itemIndex) => itemIndex !== index);
    });
  };

  const handleSaveShowNames = () => {
    const names = showNames.map((item) => String(item || '').trim());

    if (names.some((item) => !item)) {
      toast.error('Show name is required');
      return;
    }

    if (new Set(names.map((item) => item.toLowerCase())).size !== names.length) {
      toast.error('Show name must be unique');
      return;
    }

    dispatch(
      updateSingleConfigRequest({
        name: 'showNames',
        value: names,
      }),
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        width: '100%',
        border: '1px solid #e6e8ec',
        borderRadius: '12px',
        mt: 2,
        boxShadow: '0 8px 24px rgba(32, 33, 36, 0.06)',
      }}
    >
      <Typography fontWeight={700} fontSize={24} mb={1} fontFamily={'Aeonik'}>
        {t('configuration.showNames')}
      </Typography>
      <Typography mb={3} color="#7B8499">
        {t('configuration.showNamesDesc')}
      </Typography>

      {showNames.map((name, index) => (
        <Box
          key={`show-name-${index}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: canSave ? '1fr auto' : '1fr',
            },
            gap: 2,
            mb: 2,
            alignItems: 'flex-start',
          }}
        >
          <TextField
            label={t('configuration.showName')}
            size="small"
            fullWidth
            value={name}
            disabled={!canSave}
            onChange={(e) => updateShowName(index, e.target.value)}
          />
          {canSave && (
            <Button
              variant="contained"
              onClick={() => handleRemoveShowName(index)}
              sx={{
                height: 40,
                px: 2.5,
                bgcolor: '#D93025',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#B3261E', boxShadow: 'none' },
              }}
            >
              {t('configuration.remove')}
            </Button>
          )}
        </Box>
      ))}

      {canSave && (
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <Button
            variant="outlined"
            sx={{
              textTransform: 'none',
              height: 40,
              px: 2.5,
              fontWeight: 600,
              borderColor: '#752ADA',
              color: '#752ADA',
            }}
            onClick={handleAddShowName}
          >
            {t('configuration.addShowName')}
          </Button>
          <Button
            variant="contained"
            sx={{
              bgcolor: '#752ADA',
              textTransform: 'none',
              height: 40,
              px: 2.5,
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#641fbe', boxShadow: 'none' },
            }}
            onClick={handleSaveShowNames}
          >
            {t('configuration.save')}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default ShowNames;
